 import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Vote } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { LazyImage } from './LazyImage';
import { ContestVoteButton } from '../contest/ContestVoteButton';
import { getOptimizedImageUrl } from '../../lib/imageUtils';

export const TopFiveGlobal: React.FC = () => {
  const { data: topContestants, isLoading: isLoadingTop } = useQuery({
    queryKey: ['topGlobalFive'],
    queryFn: async () => {
      // Utilisation de la fonction optimisée Supabase
      const { data, error } = await supabase.rpc('get_top_contestants_v2');
      if (error) throw error;
      if (!data) return [];
      // Trier et prendre les 5 meilleurs candidats
      return data
        .sort((a: any, b: any) => Number(b.votes_count) - Number(a.votes_count))
        .slice(0, 5)
        .map((item: any) => ({
          id: item.contestant_id,
          name: item.contestant_name,
          unique_code: item.unique_code,
          votes_count: Number(item.votes_count),
          contest_name: item.contest_name,
          contest_slug: item.contest_slug,
          photo_url: item.photo_url,
        }));
    },
    refetchInterval: 30000,
    staleTime: 15000,
    // cacheTime: 60000, // Supprimé car non supporté par useQuery
  });

  if (isLoadingTop || !topContestants || topContestants.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-400/5 to-transparent opacity-50"></div>
      <div className="relative">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
            <Crown className="h-5 w-5 text-yellow-400" />
            <span className="text-white font-medium">Top 5 Global</span>
          </div>
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 bg-clip-text text-transparent animate-gradient-x">
            Candidats les plus populaires sur VotAfrica
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {topContestants.map((contestant: any, index: number) => (
            <div key={contestant.id} className="group relative">
              <div className="absolute -inset-1 bg-white/10 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full flex flex-col items-center text-center">
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.2)] flex items-center gap-2">
                  <Crown className="h-4 w-4 text-yellow-400" />
                  <span className="text-white text-sm font-bold">#{index + 1}</span>
                </div>

                <Link
                  to={`/${contestant.unique_code}`}
                  className="relative block w-32 h-32 mb-6 transform group-hover:scale-110 transition-all duration-500"
                >
                  <LazyImage
                    src={getOptimizedImageUrl(contestant.photo_url, 128, 128)}
                    alt={contestant.name}
                    className="w-full h-full object-cover rounded-full border-2 border-white/30 group-hover:border-white/50 transition-colors duration-500"
                  />
                </Link>

                <Link
                  to={`/${contestant.unique_code}`}
                  className="text-lg font-bold text-white mb-2 hover:text-gray-300 transition-colors"
                >
                  {contestant.name}
                  {contestant.number && (
                    <span className="flex flex-col items-center ml-2">
                      <span className="bg-yellow-300 text-black font-bold text-xs px-2 py-0.5 rounded-t-lg rounded-b-md shadow-md uppercase tracking-widest mb-0.5" style={{letterSpacing: '0.12em'}}>Numéro</span>
                      <span className="bg-white text-yellow-500 font-extrabold text-2xl px-3 py-0.5 rounded-full border-4 border-yellow-300 shadow-xl drop-shadow-xl ml-1" style={{lineHeight: '1', letterSpacing: '0.08em'}}>{contestant.number}</span>
                    </span>
                  )}
                </Link>

                <Link
                  to={`/c/${contestant.contest_slug}`}
                  className="text-sm text-gray-400 mb-4 hover:text-white transition-colors"
                >
                  {contestant.contest_name}
                </Link>

                <div className="mb-4">
                  <ContestVoteButton uniqueCode={contestant.unique_code} contestantId={contestant.id} />
                </div>

                <div className="flex items-center justify-center gap-2 text-white">
                  <Vote className="h-4 w-4" />
                  <span className="font-medium">{contestant.votes_count} votes</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
